import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { brand, nav, cta } from "@/content/site";
import { Btn } from "@/components/primitives";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-white/85 backdrop-blur-lg border-b border-border-neutral"
          : "bg-transparent"
      )}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center justify-between h-16 md:h-24">
          <Link
            to="/"
            data-testid="header-logo-link"
            className="flex items-center shrink-0"
            aria-label="Green Circle Group home"
          >
            <img
              src={brand.logo}
              alt="Green Circle Group logo"
              className="h-10 md:h-14 w-auto"
              width="1900"
              height="900"
            />
          </Link>

          <nav
            className="hidden lg:flex items-center gap-1"
            aria-label="Primary"
          >
            {nav.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.to.replace(/\//g, "") || "home"}`}
                  className={cn(
                    "px-4 py-2 rounded-full text-sm font-semibold transition-colors",
                    active
                      ? "text-forest bg-soft"
                      : "text-mid-neutral hover:text-forest hover:bg-soft"
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:block">
            <Btn to={cta.primary.to} size="sm" data-testid="header-cta">
              {cta.primary.label}
            </Btn>
          </div>

          {/* Mobile */}
          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-trigger"
                  aria-label="Open menu"
                  className="inline-flex items-center justify-center h-11 w-11 rounded-full border border-border-neutral bg-white text-forest"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[86%] sm:w-[380px] bg-white p-0 border-l border-border-neutral"
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between p-5 border-b border-border-neutral">
                    <img
                      src={brand.logo}
                      alt="Green Circle Group logo"
                      className="h-10 w-auto"
                    />
                    <button
                      onClick={() => setOpen(false)}
                      aria-label="Close menu"
                      data-testid="mobile-menu-close"
                      className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-soft text-forest"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <nav
                    className="flex-1 overflow-y-auto p-5 flex flex-col gap-1"
                    aria-label="Mobile"
                  >
                    <Link
                      to="/"
                      className="px-4 py-3.5 rounded-xl text-lg font-bold text-forest hover:bg-soft"
                    >
                      Home
                    </Link>
                    {nav.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        data-testid={`mobile-nav-${item.to.replace(/\//g, "")}`}
                        className="px-4 py-3.5 rounded-xl text-lg font-bold text-forest hover:bg-soft"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </nav>
                  <div className="p-5 border-t border-border-neutral">
                    <Btn to={cta.primary.to} className="w-full" data-testid="mobile-cta">
                      {cta.primary.label}
                    </Btn>
                    <a
                      href={`mailto:${brand.email}`}
                      className="block text-center mt-3 text-sm font-semibold text-mid-neutral"
                    >
                      {brand.email}
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
